/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { ClusterCtlProtoGetClusterDeploymentOptionsRes } from "../../application/protocol/cluster-controller";
import {
    ClusterPresetProto,
    ClusterPresetProtoClusterPresetSpecProto,
    MachineTypeProto,
    MachineTypeProtoPaymentOptions,
    ProviderProto,
    RegionProto,
    StorageTypeProto,
    StorageTypeProtoFeature,
    TypeDBVersionProto
} from "../../protocol/concept";
import { SnapshotConfiguration, snapshotConfigurationOf } from "./cluster";

export interface ClusterDeploymentOptions {
    providers: ProviderOptions[];
    typeDBVersions: TypeDBVersion[];
}

export interface Provider {
    id: string;
    name: string;
}

export interface ProviderOptions extends Provider {
    regions: RegionOptions[];
    isFreeAvailable: boolean;
}

export function getPaymentOptions(
    clusterDeploymentOptions: ProviderOptions[], providerId: string,
    regionId: string | undefined, machineTypeVendorId: string | undefined
): PaymentOptions[] {
    const providerOptions = clusterDeploymentOptions.find(x => x.id == providerId);
    if (!providerOptions) return [];
    if (!regionId) return [...new Set(providerOptions.regions.flatMap(region => region.machineTypes.flatMap(machineType => machineType.payableWith)))];
    const regionOptions = providerOptions.regions.find(x => x.id == regionId);
    if (!regionOptions) return [];
    if (!machineTypeVendorId) return [...new Set(regionOptions.machineTypes.flatMap(machineType => machineType.payableWith))];
    const machineType = regionOptions.machineTypes.find(x => x.vendorId == machineTypeVendorId);
    if (!machineType) return [];
    return machineType.payableWith;
}

export interface Region {
    id: string;
    continentName: string;
    continentOrdinal: number;
    countryCode: string;
    locationName: string;
    isFreeAvailable: boolean;
    vendorId: string;
}

export function regionToString(region: Region, form: "short" | "long" = "long"): string {
    return form === "long" ? `${region.locationName} (${region.vendorId})` : region.vendorId;
}

export interface RegionOptions extends Region {
    machineTypes: MachineTypeOption[];
    storageTypes: StorageType[];
    clusterPresets: ClusterPreset[];
}

export const burstableTooltip = "Burstable vCPUs throttle when underutilized, and temporarily burst to full capacity when required.";

export class MachineType {
    vendorId: string;
    cpu: number;
    memGB: number;
    isBurstable: boolean;
    payableWith: PaymentOptions[];

    constructor(vendorId: string, cpu: number, memGB: number, isBurstable: boolean, payableWith: PaymentOptions[]) {
        this.vendorId = vendorId;
        this.cpu = cpu;
        this.memGB = memGB;
        this.isBurstable = isBurstable;
        this.payableWith = payableWith;
    }

    toProto(): MachineTypeProto {
        return new MachineTypeProto({ vendorId: this.vendorId });
    }
}

export class MachineTypeOption extends MachineType {
    isFree: boolean;

    constructor(vendorId: string, cpu: number, memGB: number, isBurstable: boolean, payableWith: PaymentOptions[], isFree: boolean) {
        super(vendorId, cpu, memGB, isBurstable, payableWith);
        this.isFree = isFree;
    }

    override toProto(): MachineTypeProto {
        return new MachineTypeProto({ vendorId: this.vendorId, isFree: this.isFree });
    }
}

export function sortMachineType(a: MachineTypeOption, b: MachineTypeOption) {
    return (a.cpu - b.cpu) || (a.memGB - b.memGB) || (Number(b.isBurstable) - Number(a.isBurstable)) || (Number(b.isFree) - Number(a.isFree)) || (a.vendorId.localeCompare(b.vendorId));
}

export function machineTypeComputeString(machine: MachineType): string {
    return `${machineTypeCpuString(machine)}, ${machineTypeRamString(machine)}`;
}

export function machineTypeCpuString(machine: MachineType): string {
    if (machine.isBurstable) return `${machine.cpu} burstable vCPU`;
    return `${machine.cpu} vCPU`;
}

export function machineTypeRamString(machine: MachineType): string {
    return `${machine.memGB} GB RAM`;
}

export function machineTypeToString(machine: MachineType): string {
    return `${machineTypeComputeString(machine)} (${machine.vendorId})`;
}

export type StorageFeature = "snapshot" | "expansion";
export interface StorageType {
    vendorId: string;
    vendorName: string;
    features: StorageFeature[];
    gbMin: number;
    gbMax: number;
    iops: string;
}

export function storageTypeToString(storage: StorageType): string {
    return `${storage.iops} (${storage.vendorName})`;
}

export interface TypeDBVersion {
    version: string;
    sampleDatasets: string[];
    singleNodeOnly: boolean;
}

export interface ClusterPreset {
    id: string;
    name: string;
    singleNodeSpec: ClusterPresetSpec;
    multiNodeSpec: ClusterPresetSpec;
}

export interface ClusterPresetSpec {
    machineType: MachineTypeOption;
    storageType: StorageType;
    storageGB: number;
    serverCount: number;
    snapshotConfiguration: SnapshotConfiguration;
}

export enum PaymentOptions {
    CARD, MARKETPLACE, CREDITS
}

export function clusterDeploymentOptionsOf(options: ClusterCtlProtoGetClusterDeploymentOptionsRes): ClusterDeploymentOptions {
    return {
        providers: [options.gcp, options.aws, options.azure].filter(x => !!x).map(providerOptionsOf),
        typeDBVersions: options.typedbVersions.map(typeDBVersionOf),
    };
}

export function providerOf(provider: ProviderProto): Provider {
    return {
        id: provider.id,
        name: provider.name,
    };
}

export function providerProtoOf(provider: Provider): ProviderProto {
    return new ProviderProto({
        id: provider.id,
        name: provider.name,
    });
}

export function providerOptionsOf(provider: ProviderProto): ProviderOptions {
    const regions = provider.regions.map((region) => regionOptionsOf(region))
    return {
        id: provider.id,
        name: provider.name,
        isFreeAvailable: regions.some(region => region.isFreeAvailable),
        regions,
    };
}

export function regionOf(region: RegionProto): Region {
    return {
        id: region.id,
        continentName: region.continentName,
        continentOrdinal: region.continentOrdinal,
        countryCode: region.countryCode,
        locationName: region.locationName,
        isFreeAvailable: region.machineTypes.some(machine => machine.isFree),
        vendorId: region.vendorId,
    };
}

export function regionProtoOf(region: Region): RegionProto {
    return new RegionProto({
        id: region.id,
        countryCode: region.countryCode,
        locationName: region.locationName,
        vendorId: region.vendorId
    });
}

function regionOptionsOf(region: RegionProto): RegionOptions {
    return {
        id: region.id,
        continentName: region.continentName,
        continentOrdinal: region.continentOrdinal,
        countryCode: region.countryCode,
        locationName: region.locationName,
        isFreeAvailable: region.machineTypes.some(machine => machine.isFree),
        vendorId: region.vendorId,
        machineTypes: region.machineTypes.map(machineTypeOptionOf),
        storageTypes: region.storageTypes.map(storageTypeOf),
        clusterPresets: region.clusterPresets.map(clusterPresetOf),
    }
}

export function machineTypeOf(machineType: MachineTypeProto): MachineType {
    return new MachineType(
        machineType.vendorId,
        machineType.cpu,
        machineType.ramGb,
        machineType.isBurstable,
        machineType.payableWith.map(paymentOptionOf),
    );
}

export function machineTypeOptionOf(machineType: MachineTypeProto): MachineTypeOption {
    return new MachineTypeOption(
        machineType.vendorId,
        machineType.cpu,
        machineType.ramGb,
        machineType.isBurstable,
        machineType.payableWith.map(paymentOptionOf),
        machineType.isFree,
    );
}

function storageFeatureOf(feature: StorageTypeProtoFeature): StorageFeature {
    switch (feature) {
        case StorageTypeProtoFeature.SNAPSHOT: return "snapshot";
        case StorageTypeProtoFeature.EXPANSION: return "expansion";
    }
}

export function storageTypeOf(storageType: StorageTypeProto): StorageType {
    return {
        vendorId: storageType.vendorId,
        vendorName: storageType.vendorName,
        features: storageType.features.map(storageFeatureOf),
        gbMin: storageType.minGb,
        gbMax: storageType.maxGb,
        iops: storageType.iops,
    };
}

export function storageTypeProtoOf(storageType: StorageType): StorageTypeProto {
    return new StorageTypeProto({ vendorId: storageType.vendorId });
}

export function typeDBVersionOf(typeDBVersion: TypeDBVersionProto): TypeDBVersion {
    return {
        version: typeDBVersion.version,
        sampleDatasets: typeDBVersion.sampleDatasets,
        singleNodeOnly: typeDBVersion.singleNodeOnly,
    };
}

export function typeDBVersionProtoOf(typeDBVersion: TypeDBVersion): TypeDBVersionProto {
    return new TypeDBVersionProto({
        version: typeDBVersion.version,
        sampleDatasets: typeDBVersion.sampleDatasets
    });
}

export function clusterPresetOf(data: ClusterPresetProto): ClusterPreset {
    return {
        id: data.id,
        name: data.name,
        singleNodeSpec: clusterPresetSpecOf(data.singleNodeSpec),
        multiNodeSpec: clusterPresetSpecOf(data.multiNodeSpec),
    };
}

function clusterPresetSpecOf(data: ClusterPresetProtoClusterPresetSpecProto): ClusterPresetSpec {
    return {
        machineType: machineTypeOptionOf(data.machineType),
        storageType: storageTypeOf(data.storageType),
        storageGB: data.storageGb,
        serverCount: data.serverCount,
        snapshotConfiguration: snapshotConfigurationOf(data.snapshotConfiguration)
    };
}

export function paymentOptionOf(data: MachineTypeProtoPaymentOptions): PaymentOptions {
    switch (data) {
        case MachineTypeProtoPaymentOptions.CARD: return PaymentOptions.CARD
        case MachineTypeProtoPaymentOptions.MARKETPLACE: return PaymentOptions.MARKETPLACE
        case MachineTypeProtoPaymentOptions.CREDITS: return PaymentOptions.CREDITS
    }
}
